import { usePlaybackCurrentTs } from '@app/core/playback/hooks/usePlayback';
import { nlpIntegration } from '@mi-front/api-clients';
import { toTimestamp } from '@mi-front/core';
import { StreamEl } from '@mi-front/ui';
import { getTopicDisplayTitle } from '../../helpers/get-topic-display-title';
import { getCategoryColor } from '../../shared/topic-category-chip/category-colors';
import { StickyPillLabel, TopicPill } from './elements';

/**
 * Renders a single topic segment on the Live Topics track: pill with display title (refined or canonical).
 * When onMouseEnter/onMouseLeave are provided, the parent handles hover (e.g. for a custom popup).
 * When omitted, the parent typically wraps this in HoverPopover so the popover handles hover.
 * The pill is focused when the playback playhead is within the segment interval, or when isFocused is passed.
 */
export function TopicSegmentPill({
  topic,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isFocused: isFocusedProp,
}: {
  topic: nlpIntegration.TopicOutDto;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
  /** When set, clicking the pill triggers this (e.g. play at segment start). */
  onClick?: () => void;
  isFocused?: boolean;
}) {
  const currentTs = usePlaybackCurrentTs();
  const startTs = toTimestamp(topic.start_time);
  const endTs = toTimestamp(topic.end_time);
  const isPlayheadInSegment = currentTs !== null && currentTs >= startTs && currentTs < endTs;
  const isFocused = isFocusedProp === true || isPlayheadInSegment;
  const pillColor = getCategoryColor(topic.category);

  return (
    <StreamEl ts={startTs} position="top" offsetY={4} style={{ pointerEvents: 'auto' }}>
      {(scale) => {
        const width = (scale(endTs) ?? 0) - (scale(startTs) ?? 0);

        return (
          <div
            style={{
              width: Math.max(width, 4),
              minHeight: 24,
              cursor: onClick ? 'pointer' : undefined,
            }}
            onMouseEnter={onMouseEnter ? (e) => onMouseEnter(e) : undefined}
            onMouseLeave={onMouseLeave ?? undefined}
            onClick={onClick}
          >
            <TopicPill
              color={pillColor}
              style={{
                width: width >= 60 ? width : 'auto',
                maxWidth: width >= 60 ? width : 60,
                borderLeft: '2px solid rgba(0, 0, 0, 0.2)',
              }}
              title={getTopicDisplayTitle(topic)}
              isFocused={isFocused}
            >
              {width >= 60 ? (
                <StickyPillLabel color={pillColor}>{getTopicDisplayTitle(topic)}</StickyPillLabel>
              ) : (
                '\u200E'
              )}
            </TopicPill>
          </div>
        );
      }}
    </StreamEl>
  );
}
